import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { formatCurrency, formatDate, getMonthName, getCurrentYear, toISODateString } from '../lib/utils';
import { Plus, Pencil, Trash2, CalendarIcon, TrendingUp, CalendarRange, Check, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { format, eachDayOfInterval, addDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ExportButtons } from '../components/ExportButtons';

export function ForecastCAPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [year, setYear] = useState(getCurrentYear());
  const [month, setMonth] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [multiDialogOpen, setMultiDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => { fetchData(); }, [year, month]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { annee: year };
      if (month) params.mois = month;
      const res = await api.get('/ventes-prev', { params });
      setData(res.data);
    } catch { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/ventes-prev/${id}`); toast.success('Supprimé'); selectedIds.delete(id); setSelectedIds(new Set(selectedIds)); fetchData(); } catch { toast.error('Erreur'); }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try { await api.post('/ventes-prev/delete-bulk', { ids: [...selectedIds] }); toast.success(`${selectedIds.size} supprimée(s)`); setSelectedIds(new Set()); fetchData(); } catch { toast.error('Erreur'); }
  };

  const toggleSelect = (id) => { const n = new Set(selectedIds); if (n.has(id)) n.delete(id); else n.add(id); setSelectedIds(n); };

  // Client-side date range filter + sort
  const filteredData = data.filter(d => {
    if (dateFrom && d.date < dateFrom) return false;
    if (dateTo && d.date > dateTo) return false;
    return true;
  }).sort((a, b) => {
    let cmp = 0;
    if (sortField === 'date') cmp = a.date.localeCompare(b.date);
    else if (sortField === 'ca_prevu') cmp = (a.ca_prevu || 0) - (b.ca_prevu || 0);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredData.map(d => d.id)));
  };

  const totalCA = filteredData.reduce((sum, v) => sum + (v.ca_prevu || 0), 0);

  // Simple form: date + CA prévu
  const VenteForm = ({ item, onClose }) => {
    const [form, setForm] = useState(item ? { date: item.date, ca_prevu: item.ca_prevu || 0 } : { date: toISODateString(new Date()), ca_prevu: 0 });
    const [dateOpen, setDateOpen] = useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (item?.id) { await api.put(`/ventes-prev/${item.id}`, form); toast.success('Modifié'); }
        else { await api.post('/ventes-prev', form); toast.success('Créé'); }
        fetchData(); onClose();
      } catch { toast.error('Erreur'); }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date</Label>
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.date ? format(new Date(form.date), 'PPP', { locale: fr }) : 'Choisir'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={form.date ? new Date(form.date) : undefined}
                  onSelect={(date) => { if (date) { setForm({ ...form, date: toISODateString(date) }); setDateOpen(false); } }} locale={fr} />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label>CA Prévu (EUR)</Label>
            <Input type="number" step="0.01" value={form.ca_prevu} data-testid="input-ca-prevu"
              onChange={(e) => setForm({ ...form, ca_prevu: parseFloat(e.target.value) || 0 })} />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit">{item?.id ? 'Modifier' : 'Créer'}</Button>
        </DialogFooter>
      </form>
    );
  };

  // Multi-date form: date range + daily CA
  const MultiDateForm = ({ onClose }) => {
    const [dateRange, setDateRange] = useState({ from: new Date(), to: addDays(new Date(), 6) });
    const [selectedDays, setSelectedDays] = useState([1, 2, 3, 4, 5, 6, 0]);
    const [caJour, setCaJour] = useState(0);
    const [creating, setCreating] = useState(false);
    const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

    const toggleDay = (day) => {
      if (selectedDays.includes(day)) setSelectedDays(selectedDays.filter(d => d !== day));
      else setSelectedDays([...selectedDays, day]);
    };

    const getDatesInRange = () => {
      if (!dateRange?.from || !dateRange?.to) return [];
      return eachDayOfInterval({ start: dateRange.from, end: dateRange.to }).filter(d => selectedDays.includes(d.getDay()));
    };
    const selectedDates = getDatesInRange();

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (caJour <= 0) { toast.error('Montant CA requis'); return; }
      if (selectedDates.length === 0) { toast.error('Aucune date'); return; }
      setCreating(true);
      let created = 0, updated = 0;
      for (const date of selectedDates) {
        try {
          const dateStr = toISODateString(date);
          const existing = data.find(d => d.date === dateStr);
          await api.post('/ventes-prev', { date: dateStr, ca_prevu: caJour });
          if (existing) updated++; else created++;
        } catch { /* skip */ }
      }
      setCreating(false);
      const msgs = [];
      if (created > 0) msgs.push(`${created} créée(s)`);
      if (updated > 0) msgs.push(`${updated} mise(s) à jour`);
      if (msgs.length > 0) { toast.success(msgs.join(', ')); fetchData(); onClose(); }
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-2"><CalendarRange className="w-4 h-4" /> Plage de dates</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange?.from && dateRange?.to ? `${format(dateRange.from, 'dd/MM/yyyy')} - ${format(dateRange.to, 'dd/MM/yyyy')}` : 'Sélectionner'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="range" selected={dateRange} onSelect={(range) => setDateRange(range || { from: new Date(), to: new Date() })} locale={fr} numberOfMonths={2} />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>Jours de la semaine</Label>
          <div className="flex gap-2 flex-wrap">
            {[1, 2, 3, 4, 5, 6, 0].map((day) => (
              <button type="button" key={day} onClick={() => toggleDay(day)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${selectedDays.includes(day) ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/50 border-border hover:bg-muted'}`}>
                {selectedDays.includes(day) && <Check className="w-4 h-4" />}
                <span className="text-sm font-medium">{dayNames[day]}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>CA Prévu par jour (EUR)</Label>
          <Input type="number" step="0.01" value={caJour} onChange={(e) => setCaJour(parseFloat(e.target.value) || 0)} data-testid="input-ca-jour" />
        </div>

        <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-2">
          <div className="flex justify-between"><span className="text-sm text-muted-foreground">Jours sélectionnés</span><Badge variant="secondary">{selectedDates.length} jours</Badge></div>
          <div className="flex justify-between"><span className="text-sm text-muted-foreground">CA par jour</span><span className="font-medium">{formatCurrency(caJour)}</span></div>
          <div className="flex justify-between border-t pt-2"><span className="font-medium">CA Total</span><span className="text-xl font-bold text-primary">{formatCurrency(caJour * selectedDates.length)}</span></div>
        </div>

        {selectedDates.length > 0 && selectedDates.length <= 14 && (
          <div className="text-xs text-muted-foreground">
            <p className="font-medium mb-1">Dates :</p>
            <div className="flex flex-wrap gap-1">{selectedDates.map((d, i) => <span key={i} className="px-2 py-0.5 bg-muted rounded">{format(d, 'dd/MM', { locale: fr })}</span>)}</div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
          <Button type="submit" disabled={creating || selectedDates.length === 0}>{creating ? 'Création...' : `Créer ${selectedDates.length} prévisions`}</Button>
        </DialogFooter>
      </form>
    );
  };

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title">Forecast CA (Prévisionnel)</h1>
          <p className="text-muted-foreground mt-1">Prévisions de chiffre d'affaires par jour</p>
        </div>
        <div className="flex gap-2">
          <ExportButtons api={api} moduleKey="forecast_ca" year={year} month={month} />
          <Button variant="outline" className="gap-2" onClick={() => setMultiDialogOpen(true)} data-testid="add-multi-forecast-btn"><CalendarRange className="w-4 h-4" /> Multi-dates</Button>
          <Button className="gap-2" data-testid="add-forecast-btn" onClick={() => { setEditItem(null); setDialogOpen(true); }}><Plus className="w-4 h-4" /> Nouvelle prévision</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>{[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={month?.toString() || 'all'} onValueChange={(v) => setMonth(v === 'all' ? null : parseInt(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Tous les mois" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les mois</SelectItem>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <SelectItem key={m} value={m.toString()}>{getMonthName(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Label className="text-xs whitespace-nowrap">Du</Label>
          <Input type="date" className="w-[150px] h-9" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
          <Label className="text-xs whitespace-nowrap">Au</Label>
          <Input type="date" className="w-[150px] h-9" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
          {(dateFrom || dateTo) && <Button variant="ghost" size="sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>Effacer</Button>}
        </div>
        <div className="ml-auto flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
          <TrendingUp className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Total CA Prévu:</span>
          <span className="text-lg font-bold text-primary tabular-nums">{formatCurrency(totalCA)}</span>
          <Badge variant="secondary" className="ml-1">{filteredData.length} j.</Badge>
        </div>
      </div>

      {/* Bulk delete */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-testid="bulk-delete-bar">
          <span className="text-sm font-medium">{selectedIds.size} sélectionnée(s)</span>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())}>Désélectionner</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button variant="destructive" size="sm" className="gap-1" data-testid="bulk-delete-btn"><Trash2 className="w-4 h-4" /> Supprimer</Button></AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Supprimer {selectedIds.size} prévision(s) ?</AlertDialogTitle><AlertDialogDescription>Irréversible.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={handleDeleteSelected}>Supprimer</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                    checked={filteredData.length > 0 && selectedIds.size === filteredData.length} onChange={toggleSelectAll} data-testid="select-all-checkbox" />
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('date')} data-testid="sort-date">
                  <span className="flex items-center">Date<SortIcon field="date" /></span>
                </TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => handleSort('ca_prevu')} data-testid="sort-ca-prevu">
                  <span className="flex items-center justify-end">CA Prévu<SortIcon field="ca_prevu" /></span>
                </TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Aucune prévision</TableCell></TableRow>
              ) : (
                filteredData.map(item => (
                  <TableRow key={item.id} className={selectedIds.has(item.id) ? 'bg-primary/5' : ''}>
                    <TableCell>
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 cursor-pointer accent-primary"
                        checked={selectedIds.has(item.id)} onChange={() => toggleSelect(item.id)} />
                    </TableCell>
                    <TableCell className="font-medium">{formatDate(item.date)}</TableCell>
                    <TableCell className="text-right tabular-nums font-bold text-primary">{formatCurrency(item.ca_prevu)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => { setEditItem(item); setDialogOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Supprimer ?</AlertDialogTitle><AlertDialogDescription>Irréversible.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Annuler</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)}>Supprimer</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {filteredData.length > 0 && (
                <TableRow className="bg-muted/50 font-bold">
                  <TableCell colSpan={2}>Total ({filteredData.length} jours)</TableCell>
                  <TableCell className="text-right tabular-nums text-primary">{formatCurrency(totalCA)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent><DialogHeader><DialogTitle>{editItem ? 'Modifier' : 'Nouvelle'} Prévision</DialogTitle></DialogHeader>
          <VenteForm item={editItem} onClose={() => setDialogOpen(false)} /></DialogContent>
      </Dialog>
      <Dialog open={multiDialogOpen} onOpenChange={setMultiDialogOpen}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarRange className="w-5 h-5" /> Prévisions Multi-dates</DialogTitle></DialogHeader>
          <MultiDateForm onClose={() => setMultiDialogOpen(false)} /></DialogContent>
      </Dialog>
    </div>
  );
}
